"use client";

import React from "react";
import { ArrowRight, CheckCircle2, Home, Building2, Shield, Zap, Clock } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function PageTemplateDetails() {
  const heroReveal = useRevealOnScroll({
    threshold: 0.1
  });
  const introReveal = useRevealOnScroll({
    threshold: 0.1
  });
  const tabsReveal = useRevealOnScroll({
    threshold: 0.1
  });
  const cardsReveal = useRevealOnScroll({
    threshold: 0.1
  });
  const ctaReveal = useRevealOnScroll({
    threshold: 0.1
  });
  const features = [{
    title: "Unmatched Expertise",
    description: "Decades of combined experience in delivering top-tier solutions for complex property challenges.",
    icon: Shield
  }, {
    title: "Lightning Fast Execution",
    description: "Streamlined processes ensure your projects are completed ahead of schedule without compromising quality.",
    icon: Zap
  }, {
    title: "24/7 Reliability",
    description: "Round-the-clock support and monitoring to give you complete peace of mind.",
    icon: Clock
  }, {
    title: "Sustainable Practices",
    description: "Eco-friendly approaches that protect the environment while maximizing your property's value.",
    icon: Home
  }];
  return <section id="page-template-details" className="bg-background w-full overflow-hidden pb-24">
      {}
      <div className="px-4 pt-8 md:px-8 lg:px-12">
        <div ref={heroReveal.ref} className={`bg-secondary text-secondary-foreground relative overflow-hidden rounded-lg transition-all duration-1000 ${heroReveal.isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579221/img/hero_pattern_overlay.png)] opacity-10 mix-blend-overlay"></div>
          <div className="relative z-10 mx-auto max-w-5xl px-6 py-24 text-center md:py-32 lg:py-40">
            <h1 className="font-sans text-5xl font-extrabold tracking-tight md:text-6xl lg:text-7xl">
              Mastering Your Property's <span className="text-primary">Potential</span>
            </h1>
            <p className="text-muted-foreground mx-auto mt-6 max-w-2xl text-lg md:text-xl">
              Comprehensive solutions designed to elevate your real estate assets through innovative technology and dedicated service.
            </p>
          </div>
        </div>
      </div>

      {}
      <div className="mx-auto max-w-7xl px-4 md:px-8 lg:px-12">
        <div ref={introReveal.ref} className={`grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-0 transition-all duration-1000 delay-100 ${introReveal.isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          <div className="lg:col-span-7 lg:py-[128px] lg:pr-16">
            <h2 className="text-foreground text-3xl font-bold tracking-tight md:text-4xl">
              Precision Engineering for Modern Real Estate
            </h2>
            <p className="text-muted-foreground mt-6 text-lg leading-relaxed">
              We bridge the gap between traditional property management and cutting-edge technological solutions. Our approach ensures that every asset we touch is optimized for performance, longevity, and maximum return on investment.
            </p>
            <ul className="mt-8 space-y-4">
              {["Advanced structural diagnostics", "Energy efficiency optimization", "Predictive maintenance modeling"].map((item, i) => <li key={i} data-index={i} className="flex items-center gap-3">
                  <CheckCircle2 className="text-primary size-6 shrink-0" />
                  <span className="text-foreground font-medium">{item}</span>
                </li>)}
            </ul>
            <div className="mt-10">
              <Button asChild size="lg">
                <Link to="/services">
                  Explore Our Services <ArrowRight className="ml-2 size-4" />
                </Link>
              </Button>
            </div>
          </div>
          <div className="lg:col-span-5 h-[400px] lg:h-[600px] w-full">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579221/img/technical_service_intro.png" alt="Technical property analysis" className="h-full w-full rounded-lg object-cover shadow-2xl" />
          </div>
        </div>
      </div>

      {}
      <div className="bg-muted/30 py-24">
        <div className="mx-auto max-w-5xl px-4 md:px-8 lg:px-12">
          <div ref={tabsReveal.ref} className={`text-center transition-all duration-1000 ${tabsReveal.isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
            <h2 className="text-foreground text-3xl font-bold tracking-tight md:text-4xl">
              Tailored for Your Success
            </h2>
            <p className="text-muted-foreground mt-4 text-lg">
              Select your profile to see how our solutions align with your specific goals.
            </p>

            <Tabs defaultValue="homeowners" className="mt-12">
              <TabsList className="border-border mx-auto grid w-full max-w-md grid-cols-2 border bg-transparent p-1">
                <TabsTrigger value="homeowners" className="data-[state=active]:bg-secondary data-[state=active]:text-secondary-foreground rounded-md py-3 text-base">
                  <Home className="mr-2 size-4" /> Homeowners
                </TabsTrigger>
                <TabsTrigger value="realtors" className="data-[state=active]:bg-secondary data-[state=active]:text-secondary-foreground rounded-md py-3 text-base">
                  <Building2 className="mr-2 size-4" /> Realtors
                </TabsTrigger>
              </TabsList>
              
              <div className="mt-8 rounded-xl border border-border bg-card p-8 text-left shadow-sm md:p-12">
                <TabsContent value="homeowners" className="mt-0 outline-none">
                  <div className="grid grid-cols-1 gap-8 md:grid-cols-2 items-center">
                    <div>
                      <h3 className="text-2xl font-bold text-foreground">Protect Your Biggest Investment</h3>
                      <p className="mt-4 text-muted-foreground">
                        For homeowners, peace of mind is paramount. Our comprehensive inspection and maintenance services ensure your property remains safe, efficient, and valuable for years to come. We identify potential issues before they become costly repairs.
                      </p>
                      <Button variant="outline" className="mt-6" asChild>
                        <Link to="/homeowners">Learn More for Homeowners</Link>
                      </Button>
                    </div>
                    <div className="h-64 rounded-lg bg-muted overflow-hidden">
                       <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579221/img/homeowner_focus.png" alt="Happy homeowner" className="w-full h-full object-cover" />
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="realtors" className="mt-0 outline-none">
                  <div className="grid grid-cols-1 gap-8 md:grid-cols-2 items-center">
                    <div>
                      <h3 className="text-2xl font-bold text-foreground">Accelerate Your Transactions</h3>
                      <p className="mt-4 text-muted-foreground">
                        Realtors need reliable partners to keep deals moving. Our rapid-response assessment team provides the detailed, accurate reports you need to negotiate effectively and close with confidence. We are your technical advantage in a competitive market.
                      </p>
                      <Button variant="outline" className="mt-6" asChild>
                        <Link to="/realtors">Partner With Us</Link>
                      </Button>
                    </div>
                    <div className="h-64 rounded-lg bg-muted overflow-hidden">
                       <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579221/img/realtor_focus.png" alt="Professional realtor" className="w-full h-full object-cover" />
                    </div>
                  </div>
                </TabsContent>
              </div>
            </Tabs>
          </div>
        </div>
      </div>

      {}
      <div className="relative py-24 overflow-hidden">
        {}
        <div className="absolute top-0 left-1/4 h-96 w-96 -translate-y-1/2 rounded-full bg-primary/10 blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 h-96 w-96 translate-y-1/2 rounded-full bg-secondary/10 blur-3xl"></div>
        
        <div className="mx-auto max-w-7xl px-4 md:px-8 lg:px-12 relative z-10">
          <div ref={cardsReveal.ref} className={`transition-all duration-1000 ${cardsReveal.isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
            <div className="mb-12 text-center">
              <h2 className="text-foreground text-3xl font-bold tracking-tight md:text-4xl">
                Why Choose Us
              </h2>
              <p className="text-muted-foreground mt-4 text-lg">
                The advantages that set our services apart from the competition.
              </p>
            </div>

            <ScrollArea className="w-full whitespace-nowrap pb-6">
              <div className="flex w-max space-x-6 px-1">
                {features.map((feature, i) => {
                const Icon = feature.icon;
                return <div key={i} data-index={i} className="w-[300px] shrink-0 md:w-[350px]">
                      <Card className="bg-background/60 border-border/50 h-full backdrop-blur-xl shadow-xl transition-transform hover:-translate-y-1">
                        <CardHeader>
                          <div className="bg-primary/10 text-primary mb-4 inline-flex size-12 items-center justify-center rounded-lg">
                            <Icon className="size-6" />
                          </div>
                          <CardTitle className="text-xl">{feature.title}</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <CardDescription className="text-base whitespace-normal">
                            {feature.description}
                          </CardDescription>
                        </CardContent>
                      </Card>
                    </div>;
              })}
              </div>
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
          </div>
        </div>
      </div>

      {}
      <div className="mx-auto max-w-7xl px-4 md:px-8 lg:px-12 mt-12">
        <div ref={ctaReveal.ref} className={`bg-primary rounded-lg px-6 py-16 text-center shadow-2xl md:py-24 transition-all duration-1000 ${ctaReveal.isVisible ? "scale-100 opacity-100" : "scale-95 opacity-0"}`}>
          <h2 className="text-primary-foreground text-3xl font-bold tracking-tight md:text-5xl">
            Ready to Transform Your Property?
          </h2>
          <p className="text-primary-foreground/90 mx-auto mt-6 max-w-2xl text-lg md:text-xl">
            Join hundreds of satisfied clients who have elevated their real estate assets with our expert guidance and technical solutions.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Button asChild size="lg" className="bg-transparent border-2 border-secondary text-secondary hover:bg-secondary hover:text-secondary-foreground w-full sm:w-auto">
              <Link to="/contact">
                Get Started Today
              </Link>
            </Button>
            <Button asChild size="lg" variant="link" className="text-secondary hover:text-secondary/80 w-full sm:w-auto">
              <Link to="/portfolio">
                View Our Portfolio
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}